import { bemClassBuilder } from '@vista-digital/bem-class-builder'
import { useAnimationConfig } from '@vista-digital/occ-ui/src/lib/context/occ/hooks/use-animation-config'
import { ModalFooter } from '@vista-digital/occ-ui/src/lib/subcomponents/modal/subcomponents/footer'
import { ModalHeader } from '@vista-digital/occ-ui/src/lib/subcomponents/modal/subcomponents/header'
import { ModalMode } from '@vista-digital/occ-ui/src/lib/subcomponents/modal/types/mode'
import type { ModalProps } from '@vista-digital/occ-ui/src/lib/subcomponents/modal/types/props'
import { getLastElement } from '@vista-digital/utilities'
import { observer } from 'mobx-react'
import type { MotionProps } from 'motion/react'
import { AnimatePresence, motion } from 'motion/react'
import type { FC } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

const b = bemClassBuilder('v-modal')

const animationDuration = 0.3

// We hve had to clone OCC15's version of the modal. The OCC16 version uses FocusScope, which prevents captcha from being clickable from within a modal
export const ModalUi: FC<ModalProps> = observer(props => {
    const modalRef = useRef<HTMLDivElement>(null)
    const [isAnimating, setIsAnimating] = useState(false)
    const {
        show,
        onClose,
        onOpen,
        className,
        anchoredToTop = false,
        mode = ModalMode.Auto,
        heading,
        closeButtonIconName,
        canBeClosed,
        headerImageProps,
        header,
        footerLayout,
    } = props

    useEffect(() => {
        if (!show) {
            return
        }
        onOpen?.()
    }, [show, onOpen])

    const handleClose = useCallback(() => {
        if (canBeClosed === false || !show) {
            return
        }
        onClose?.()
    }, [canBeClosed, show, onClose])

    // Makes sure if the user presses tab or shift-tab, the focus stays in the modal.
    const handleTab = (event: KeyboardEvent) => {
        if (!modalRef?.current) {
            return
        }
        const focusableModalElements: HTMLDivElement[] = Array.from(
            modalRef.current.querySelectorAll('a, button, textarea, input, select')
        )
        const firstElement = focusableModalElements[0]
        const lastElement = getLastElement(focusableModalElements)!

        if (!event.shiftKey && document.activeElement === lastElement) {
            firstElement.focus()
            event.preventDefault()
        } else if (event.shiftKey && document.activeElement === firstElement) {
            lastElement.focus()
            event.preventDefault()
        }
    }

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (isAnimating) {
                event.preventDefault()
            }
            switch (event.key) {
                case 'Escape':
                    handleClose()
                    break
                case 'Tab':
                    handleTab(event)
                    break
                default:
            }
        },
        [handleClose, isAnimating]
    )

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown, false)
        return () => {
            document.removeEventListener('keydown', handleKeyDown, false)
        }
    }, [handleKeyDown])

    useEffect(() => {
        // This prevents scrolling of the body while the modal is open.
        document.body.style.overflow = show ? 'hidden' : 'unset'
        return () => {
            document.body.style.overflow = 'unset'
        }
    }, [show])

    const { enableAnimations } = useAnimationConfig()

    const containerMotionProps: MotionProps = enableAnimations
        ? {
              initial: { opacity: 0 },
              animate: { opacity: 1 },
              transition: { duration: animationDuration },
              exit: { opacity: 0, pointerEvents: 'none' },
              onAnimationStart: () => setIsAnimating(true),
              onAnimationComplete: () => setIsAnimating(false),
          }
        : {}

    const backgroundOverlayMotionProps: MotionProps = enableAnimations
        ? {
              initial: { opacity: 0 },
              animate: { opacity: 1 },
              transition: { duration: animationDuration },
              exit: { opacity: 0 },
          }
        : {}

    return createPortal(
        <AnimatePresence onExitComplete={props.onAfterClose}>
            {show && (
                <motion.aside
                    className={b.block({ anchoredToTop }, className)}
                    aria-modal="true"
                    aria-hidden={!show}
                    tabIndex={-1}
                    role="dialog"
                >
                    <motion.div
                        {...backgroundOverlayMotionProps}
                        className={b.element('background-overlay')}
                        onClick={handleClose}
                    />
                    <motion.div {...containerMotionProps} className={b.element('container', { mode })} ref={modalRef}>
                        <ModalHeader
                            heading={heading}
                            onClose={onClose}
                            closeButtonIconName={closeButtonIconName}
                            showCloseButton={canBeClosed}
                            imageProps={headerImageProps}
                            header={header}
                        />
                        <section className={b.element('body')}>{props.children}</section>
                        <ModalFooter layout={footerLayout}>{props.footer}</ModalFooter>
                    </motion.div>
                </motion.aside>
            )}
        </AnimatePresence>,
        document.body
    )
})

ModalUi.displayName = 'ModalUi'
